import { T, tt } from '@agos/shared'
import { EmptyState, ReadFailedBlock } from '@agos/ui'
import { ShieldOff } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * The read each block reports, as the value of `data-permissions-failed` - one stem, `permissions`, for every tab.
 *
 * `roles`, `overrides` and `audit` are the three the screen had before O119 (§9.543), and `roles` is the one
 * `permissions.spec.ts` drives; the rest are the reads the new tabs added.
 */
export type PermissionsRead =
  | 'roles'
  | 'people'
  | 'overrides'
  | 'options'
  | 'holders'
  | 'delegation'
  | 'audit'

/**
 * FB-822, §9.543 - drawn per tab, never for the screen; carried to O119's five tabs (`FRONTEND.md` §9.752).
 *
 * Each tab owns its own reads and they fail independently: a working roles list beside a failed audit read must
 * keep the list, which is §9.528's decision on the customer account screen for the same reason - escalating one
 * failed read to the whole screen replaces a working surface with a fault report.
 *
 * **One call site for the whole screen**, so `read-failed-block.test.ts` counts one `permissions` stem however many
 * tabs the screen grows; the read that failed is the attribute's value.
 */
export function TabReadFailed({
  what,
  retry,
  children,
}: {
  what: PermissionsRead
  retry: () => void
  /** What is withheld, when the default sentence - about an empty rights list - is not the one at stake. */
  children?: ReactNode
}) {
  return (
    <ReadFailedBlock marker="permissions" of={what} title={tt('Nepodarilo sa načítať')} onRetry={retry}>
      {children ?? (
        <T>
          Prázdna obrazovka by znamenala, že nikto nemá žiadne práva - a to nevieme. Ukladanie je preto vypnuté.
        </T>
      )}
    </ReadFailedBlock>
  )
}

/**
 * The overview's 403 `ROLE_NOT_MANAGED` - an answer rather than a failure (§10A.408).
 *
 * Somebody whose role manages none reaches this screen only through a stale menu, and a red *could not load* would
 * tell them something is broken when the server has simply said no. No retry: asking again gets the same answer.
 */
export function NotManaged() {
  return (
    <div data-roles-not-managed className="rounded-2xl border border-border bg-card">
      <EmptyState
        icon={<ShieldOff className="w-10 h-10" />}
        title={tt('Žiadnu rolu nespravujete')}
        description={tt(
          'Role a práva upravuje hlavný administrátor a ten, koho rolu poveril spravovaním iných rolí.',
        )}
      />
    </div>
  )
}
